import React from 'react';
import en from './locales/en';
import hr from './locales/hr';
import de from './locales/de';
import fr from './locales/fr';
import es from './locales/es';
import id from './locales/id';
import it from './locales/it';
import pl from './locales/pl';
import ru from './locales/ru';
import pt from './locales/pt';
import tr from './locales/tr';
import { LANGUAGES, LANGUAGE_BY_CODE, DEFAULT_LANGUAGE, detectLanguage, languageFromPath } from './languages';

// UI strings live in ./locales/<lang>/<namespace>.json and are looked up as 'namespace.key.path'.
// Plurals are objects keyed by CLDR category (one/few/other…) and picked with t(key, { count }).
export const CATALOGS = { en, hr, de, fr, es, id, it, pl, pt, ru, tr };
export { DEFAULT_LANGUAGE };
export const INTL_LOCALES = Object.fromEntries(LANGUAGES.map(l => [l.code, l.locale]));

const initial = initialLanguage();
let activeLanguage = initial.lang;
const warned = new Set();

function storage() {
    try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

// Players who already played before any language choice existed played in English.
function isReturningPlayer(store) {
    return ['gameMode', 'wl_onboarding_completed', 'wl_onboarding_step', 'wordlabyrinth_tutorial_seen']
        .some(key => store.getItem(key));
}

// Address prefix > saved choice > returning player (English) > device language for a new visitor.
export function chooseInitialLanguage({ pathname, saved, returningPlayer, deviceLanguage }) {
    const fromPath = languageFromPath(pathname);
    if (fromPath && CATALOGS[fromPath]) return { lang: fromPath, source: 'url', save: true };
    if (saved && CATALOGS[saved]) return { lang: saved, source: 'saved', save: false };
    if (returningPlayer) return { lang: DEFAULT_LANGUAGE, source: 'saved', save: true };
    // A hidden language (live: false) is never suggested, even with a catalog; a saved choice may preview it.
    const suggestable = CATALOGS[deviceLanguage] && LANGUAGE_BY_CODE[deviceLanguage]?.live;
    return { lang: suggestable ? deviceLanguage : DEFAULT_LANGUAGE, source: 'device', save: false };
}

function initialLanguage() {
    const store = storage();
    const choice = chooseInitialLanguage({
        pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
        saved: store?.getItem('language'),
        returningPlayer: !!store && isReturningPlayer(store),
        deviceLanguage: detectLanguage(),
    });
    if (choice.save) store?.setItem('language', choice.lang);
    return choice;
}

export function getInitialLanguageSource() {
    return initial.source;
}

export function getActiveLanguage() {
    return activeLanguage;
}

export function setActiveLanguage(lang) {
    if (!CATALOGS[lang]) return;
    activeLanguage = lang;
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
}

export function intlLocale(lang = activeLanguage) {
    return INTL_LOCALES[lang] || INTL_LOCALES[DEFAULT_LANGUAGE];
}

function lookup(catalog, key) {
    let node = catalog;
    for (const part of key.split('.')) {
        if (node == null || typeof node !== 'object') return undefined;
        node = node[part];
    }
    return node;
}

const pluralRulesCache = {};
function pluralCategory(lang, count) {
    // pluralLocale: pt formats numbers as pt-BR but counts 0 as plural (pt-PT rules: "0 dicas", not "0 dica").
    if (!pluralRulesCache[lang]) {
        pluralRulesCache[lang] = new Intl.PluralRules(LANGUAGE_BY_CODE[lang]?.pluralLocale || intlLocale(lang));
    }
    return pluralRulesCache[lang].select(count);
}

function interpolate(text, vars) {
    if (!vars) return text;
    return text.replace(/\{(\w+)\}/g, (match, name) => (name in vars && vars[name] != null ? String(vars[name]) : match));
}

function resolve(lang, key, vars) {
    let value = lookup(CATALOGS[lang], key);
    if (value && typeof value === 'object') {
        const count = vars && typeof vars.count === 'number' ? vars.count : undefined;
        value = count === undefined ? value.other : (value[pluralCategory(lang, count)] ?? value.other);
    }
    return typeof value === 'string' ? value : undefined;
}

export function translate(lang, key, vars) {
    let text = resolve(lang, key, vars);
    if (text === undefined && lang !== DEFAULT_LANGUAGE) text = resolve(DEFAULT_LANGUAGE, key, vars);
    if (text === undefined) {
        if (process.env.NODE_ENV !== 'production' && !warned.has(key)) {
            warned.add(key);
            console.warn(`i18n: missing key "${key}"`);
        }
        return key;
    }
    return interpolate(text, vars);
}

// For code outside React (services, contexts' plain helpers); components should use useTranslation().
export function t(key, vars) {
    return translate(activeLanguage, key, vars);
}

// Renders a translated string with inline markup: 'Turn <b>{from}</b> into…' + components={{ b: <strong /> }}.
export function renderRich(text, components = {}) {
    const out = [];
    const re = /<(\w+)>(.*?)<\/\1>/gs;
    let last = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
        if (match.index > last) out.push(text.slice(last, match.index));
        const el = components[match[1]];
        out.push(el ? React.cloneElement(el, { key: out.length }, match[2]) : match[2]);
        last = re.lastIndex;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

export function makeTranslator(lang) {
    const tr = (key, vars) => translate(lang, key, vars);
    tr.rich = (key, components, vars) => renderRich(translate(lang, key, vars), components);
    return tr;
}

export function formatNumber(value, options, lang = activeLanguage) {
    return new Intl.NumberFormat(intlLocale(lang), options).format(value);
}

export function formatDate(value, options, lang = activeLanguage) {
    const date = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat(intlLocale(lang), options).format(date);
}
